/**
 * Firebase Main Application Configuration
 *
 * This file handles Firebase initialization for the main application context.
 * It runs in the browser's main thread and provides:
 * - Foreground message handling
 * - FCM token generation and management
 * - Integration with React components
 *
 * Note: Firebase is also initialized in the service worker (firebase-messaging-sw.js)
 * for background message handling. This is necessary because:
 * 1. Service workers run in a separate thread
 * 2. They cannot access the main app's Firebase instance
 * 3. They handle notifications when the app is closed/backgrounded
 *
 * Both initializations use shared configuration from firebaseConfig.js
 */

import { initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";
import { firebaseConfig, vapidKey } from "./firebaseConfig";

// Initialize Firebase for main application context
const app = initializeApp(firebaseConfig);

// Initialize Firebase Cloud Messaging and get a reference to the service
let messaging = null;

// Check if messaging is supported in this environment
const initializeMessaging = async () => {
  try {
    const supported = await isSupported();

    if (supported) {
      messaging = getMessaging(app);
      return messaging;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

// Get FCM registration token
export const getFCMToken = async () => {
  try {

    if (!messaging) {
      messaging = await initializeMessaging();
    }

    if (!messaging) {
      throw new Error("Firebase messaging is not supported");
    }


    if (!vapidKey) {
      // Try without VAPID key first (some setups work without it)
      const token = await getToken(messaging);
      if (token) {
        return token;
      }
      throw new Error("VAPID key is required but not configured");
    }

    const token = await getToken(messaging, {
      vapidKey: vapidKey,
    });

    if (token) {
      return token;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

// Handle foreground messages
export const onMessageListener = () => {
  return new Promise((resolve) => {
    if (!messaging) {
      initializeMessaging().then((msg) => {
        if (msg) {
          onMessage(msg, (payload) => {
            resolve(payload);
          });
        }
      });
    } else {
      onMessage(messaging, (payload) => {
        resolve(payload);
      });
    }
  });
};

// Subscribe to foreground messages with a persistent handler. Returns an unsubscribe function.
export const subscribeToForegroundMessages = async (handler) => {
  if (!messaging) {
    messaging = await initializeMessaging();
  }
  if (!messaging) return () => {};
  // onMessage returns an unsubscribe function
  const unsubscribe = onMessage(messaging, (payload) => {
    try {
      handler && handler(payload);
    } catch (e) {
    }
  });
  return unsubscribe;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  try {

    if (Notification.permission === "granted") {
      return true;
    }

    if (Notification.permission === "denied") {
      return false;
    }

    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

export { messaging };
export default app;
